import React from 'react'
import { PageErrorNoRouting } from '../../pages/PageError'

/** Host / build configuration only (no tenant identity). */
export interface AppTenantlessDeployment {
    domain: string
    environment: string
    instance: string
    environmentType: string
    clearEnvironment: () => void
    setEnvironment: (environment: string) => void
    clearInstance: () => void
    setInstance: (instance: string) => void
}

const getDomain = () => {
    const host = window.location.hostname
    const port = window.location.port
    const scheme = window.location.protocol
    const domain = `${scheme}//${host}${port ? ':' + port : ''}`
    return domain
}

const allowedEnvironments = {
    dev: ['dev', 'local', 'rc', 'qa'],
    prod: ['prod', 'sandbox'],
}

const getEnvironmentType = () => {
    const env = process.env.REACT_APP_ENV_TYPE
    if (!env) {
        throw new Error('Environment type is not set in build')
    }
    return env
}

/** Staging is host-derived (subdomain), not a selectable environment. */
export const isStaging = () => {
    const domain = getDomain()
    const subdomain = domain.split('.')[0]
    return subdomain.includes('-staging')
}

/** Read default environment from build/host (exported for coordinated reset with tenant bootstrap). */
export const readStoredEnvironment = () => {
    return process.env.REACT_APP_ENV ?? null
}

const assertEnvironment = (environment?: string | null) => {
    if (!environment) {
        throw new Error('Environment is not set')
    }
    const environmentType = getEnvironmentType()
    if (!allowedEnvironments[environmentType as keyof typeof allowedEnvironments].includes(environment)) {
        throw new Error(`Environment ${environment} is not allowed on this build`)
    }
}

const tenantlessDeploymentContext = React.createContext<AppTenantlessDeployment | undefined>(undefined)

export const useTenantlessDeploymentContext = () => {
    const contextInstance = React.useContext(tenantlessDeploymentContext)
    if (contextInstance === undefined) {
        throw new Error('useTenantlessDeploymentContext must be used within AppTenantlessDeploymentProvider')
    }
    return contextInstance
}

export const AppTenantlessDeploymentProvider = (props: { children: React.ReactNode }) => {
    const [environment, setEnvironment] = React.useState(readStoredEnvironment())
    /** Instance is taken from the tenanted URL (`/_displayId-<digits>`); org-picker dev UI may set this in-memory only. */
    const [instance, setInstance] = React.useState('')

    let value: AppTenantlessDeployment | null = null
    try {
        const domain = getDomain()
        const environmentType = getEnvironmentType()
        assertEnvironment(environment)
        value = {
            environment: environment ?? '',
            instance: instance ?? '',
            domain,
            environmentType,
            clearEnvironment: () => {
                setEnvironment(readStoredEnvironment())
            },
            setEnvironment: (env: string) => {
                setEnvironment(env)
            },
            clearInstance: () => {
                setInstance('')
            },
            setInstance: (inst: string) => {
                setInstance(inst)
            },
        }
    } catch (e) {
        console.error('Error getting environment', e)
        return <PageErrorNoRouting error={'A configuration error occurred - please contact support'} />
    }

    console.log('Deployment (minus tenant)', { environment: value.environment, instance: value.instance })

    return (
        <tenantlessDeploymentContext.Provider value={value}>{props.children}</tenantlessDeploymentContext.Provider>
    )
}
